import React, { useCallback, useEffect, useMemo, useState } from "react";
import TextBox from "../../../../components/forms/text-box";
import TextArea from "../../../../components/forms/text-area";
import DisplayTexts from "../../common/components/display-texts";
import SettingTip from "../../common/components/setting-tip";
import SlotSelector from "../../common/components/slot-selector";
import MultiLang from "../../common/components/multi-lang";
import { QUESTION_PROMPT } from "../../common/const/settings-type";
import AllowedFileTypes from "../../common/components/allowed-file-types";
import { DOC_CAPTURE_PROMPT_TYPE } from "../../common/const/doc-capture-const";

const EXTRACTION_SCHEMA_PLACEHOLDER = `{
  "invoice_number": "the invoice reference",
  "total_amount": "grand total including tax",
  "issued_date": "date on the invoice, as YYYY-MM-DD"
}`;

function DocCapturePromptSettings({ name, onChangeData, savedSettings, metadata }) {
  const [formData, setFormData] = useState(savedSettings);

  useEffect(() => {
    formData.stateType = QUESTION_PROMPT;
    // `type` is what the runtime dispatches on (handle_prompt). It normally
    // arrives from the toolbox's defaultConfig, but a step created before this
    // node existed - or through any path that did not carry defaultConfig -
    // would otherwise save without it and be executed as a plain text prompt.
    formData.type = DOC_CAPTURE_PROMPT_TYPE;
    onChangeData(formData);
  }, [formData]);

  const handleChange = useCallback(event => {
    const { name, value } = event.target;

    setFormData(prevFormData => {
      if (prevFormData[name] === value) return prevFormData;
      return {
        ...prevFormData,
        [name]: value
      };
    });
  }, []);

  const setAllowedFileTypes = useCallback(allowedFileTypes => {
    setFormData(prevFormData => ({ ...prevFormData, allowedFileTypes }));
  }, []);

  const memoizedDisplayTextOptions = useMemo(
    () => formData.displayTextOptions,
    [formData.displayTextOptions]
  );

  return (
    <div className="space-y-3">
      <SettingTip text="Asks the user to upload a document. The file is read and the fields you list below are extracted into the variable as { raw, data }." />

      <TextBox
        label="Name"
        type="text"
        name="name"
        placeholder="State name"
        value={formData.name}
        onChange={handleChange}
      />

      <SlotSelector
        value={formData.slot}
        slots={metadata.slots}
        onChange={handleChange}
        showCaptureFromUserInput={false}
      />

      <DisplayTexts
        parameterName={formData.slot}
        displayTextOptionsProps={memoizedDisplayTextOptions}
        responseModifyByAI={formData.responseModifyByAI}
        onChangeData={handleChange}
      />

      <MultiLang
        name="lang_displayTextOptions"
        rootClassName="pt-2"
        value={formData.lang_displayTextOptions || {}}
        parameterName={formData.slot}
        responseModifyByAI={formData.responseModifyByAI}
        onChangeData={handleChange}
        showBottomDivider={false}
      />

      <div className="pt-2">
        <AllowedFileTypes value={formData.allowedFileTypes} onChange={setAllowedFileTypes} />
      </div>

      <TextArea
        rootClass="pt-2"
        label="Fields to Extract"
        name="extractionSchema"
        id="extractionSchema"
        rows={8}
        placeholder={EXTRACTION_SCHEMA_PLACEHOLDER}
        value={formData.extractionSchema || ''}
        onChange={handleChange}
        subLabel="The fields to read out of the document, as a JSON object of field name to description - or just describe them in plain English. Anything the document does not contain comes back as null."
      />
    </div>
  );
}

export default DocCapturePromptSettings;
